<template>
  <div class="ism-tab-body">
    <!-- Skeleton -->
    <template v-if="loading && !society">
      <div class="ism-stat-row" style="margin-bottom: 28px">
        <div
          v-for="i in 4"
          :key="i"
          class="ism-shimmer"
          style="height: 90px; border-radius: 14px; flex: 1; min-width: 100px"
        ></div>
      </div>
    </template>

    <template v-else>
      <!-- Stat Cards -->
      <div class="ism-stat-row">
        <div
          class="stat-card"
          style="--card-bar: #f26522; --icon-bg: rgba(242, 101, 34, 0.1)"
        >
          <div class="stat-card-top">
            <div class="stat-card-label">Houses</div>
            <div class="stat-card-icon">
              <v-icon color="#F26522" size="20">mdi-home-city-outline</v-icon>
            </div>
          </div>
          <div class="stat-card-value">{{ society.no_of_house || 0 }}</div>
          <div class="stat-card-delta">Total residential houses</div>
        </div>

        <div
          class="stat-card"
          style="--card-bar: #3b82f6; --icon-bg: rgba(59, 130, 246, 0.1)"
        >
          <div class="stat-card-top">
            <div class="stat-card-label">Shops</div>
            <div class="stat-card-icon">
              <v-icon color="#3b82f6" size="20">mdi-store-outline</v-icon>
            </div>
          </div>
          <div class="stat-card-value">{{ society.no_of_shop || 0 }}</div>
          <div class="stat-card-delta">Total commercial shops</div>
        </div>

        <div
          class="stat-card"
          style="--card-bar: #10b981; --icon-bg: rgba(16, 185, 129, 0.1)"
        >
          <div class="stat-card-top">
            <div class="stat-card-label">Type</div>
            <div class="stat-card-icon">
              <v-icon color="#10b981" size="20">mdi-domain</v-icon>
            </div>
          </div>
          <div class="stat-card-value" style="font-size: 16px">
            {{ residentType(society.type) }}
          </div>
          <div class="stat-card-delta">Society category</div>
        </div>
      </div>

      <!-- Society Insights -->
      <div class="ism-insights-wrap">
        <div class="ism-insights-header">
          <div class="ism-insights-title">
            <v-icon size="16" color="var(--accent)">mdi-chart-box-outline</v-icon>
            <span>Society Insights</span>
          </div>
        </div>

        <!-- Module counter cards -->
        <div class="ism-insights-grid">
          <div
            v-for="mod in analyticsModules"
            :key="mod.key"
            class="insight-card"
            :style="`--mc: ${mod.color}; --mc-rgb: ${mod.rgb}`"
          >
            <div class="insight-card-inner">
              <div class="insight-icon-wrap">
                <v-icon size="20" color="var(--mc)">{{ mod.icon }}</v-icon>
              </div>
              <div class="insight-count-wrap">
                <div class="insight-count" style="font-size:18px;">
                  {{ society[mod.key] !== undefined ? society[mod.key] : 0 }}
                  <template v-if="mod.key === 'members_count' && society.unit_count">
                    <span class="ism-plan-suffix">
                      ({{ society.unit_count }}
                      {{ society.unit_count == 1 ? 'Unit' : 'Units' }})
                    </span>
                  </template>
                </div>
                <div class="insight-label">{{ mod.label }}</div>
              </div>
            </div>
            <div class="insight-bar-accent"></div>
          </div>

          <!-- Storage card as 8th grid slot -->
          <div class="insight-card insight-card--storage" style="--mc: #8b5cf6; --mc-rgb: 139,92,246">
            <div class="insight-card-inner" style="flex-direction:column;align-items:flex-start;gap:10px;padding:14px 16px;">
              <div style="display:flex;align-items:center;gap:10px;width:100%;">
                <div class="insight-icon-wrap">
                  <v-icon size="20" color="#8b5cf6">mdi-database-outline</v-icon>
                </div>
                <div class="insight-count-wrap">
                  <div class="insight-count" style="font-size:18px;">
                    {{ society.storage_count !== undefined ? formatStorage(society.storage_count) : '0 MB' }}
                  </div>
                  <div class="insight-label">Server Storage</div>
                </div>
                <div v-if="society.storage_limit_mb" class="insight-pct" :class="storagePercent>85?'danger':storagePercent>60?'warn':'safe'">
                  {{ storagePercent }}%
                </div>
              </div>
              <div v-if="society.storage_limit_mb" style="width:100%;">
                <div class="ism-storage-track" style="margin:0;">
                  <div
                    class="ism-storage-fill"
                    :class="storagePercent>85?'danger':storagePercent>60?'warning':'safe'"
                    :style="`width:${Math.min(storagePercent,100)}%`"
                  ></div>
                </div>
                <div v-if="storagePercent>75" class="ism-upgrade-nudge" style="margin-top:4px;">
                  <v-icon size="11" :color="storagePercent>85?'#ef4444':'#f59e0b'">mdi-alert-outline</v-icon>
                  <span>Upgrade plan</span>
                </div>
              </div>
              <div v-if="society.file_count !== undefined" style="font-size:10.5px;color:var(--text-secondary);margin-top:-4px;">{{ society.file_count || 0 }} files</div>
            </div>
            <div class="insight-bar-accent"></div>
          </div>
        </div>
      </div>

      <!-- Main Info Sections -->
      <div class="ism-detail-grid">

        <!-- Left Column: Society Info + Contact Info stacked -->
        <div class="ism-left-column">

          <!-- Society Info -->
          <div class="ism-detail-panel">
            <div class="ism-panel-title" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
              <div>
                <v-icon size="16" color="var(--accent)" class="mr-2">mdi-office-building-outline</v-icon>
                Society Information
              </div>
              <div style="display: flex; gap: 8px;">
                <v-btn
                  color="var(--accent)"
                  small
                  elevation="0"
                  outlined
                  style="border-radius: 8px; text-transform: none; font-weight: 600; letter-spacing: 0.3px; border-width: 1.5px;"
                  @click="openAddUnitModal"
                >
                  <v-icon size="16" class="mr-1">mdi-home-plus-outline</v-icon>
                  Add Unit
                </v-btn>
                <v-btn
                  color="var(--accent)"
                  class="white--text"
                  small
                  elevation="0"
                  style="border-radius: 8px; text-transform: none; font-weight: 600; letter-spacing: 0.3px;"
                  @click="openAddMemberModal"
                >
                  <v-icon size="16" class="mr-1">mdi-account-plus-outline</v-icon>
                  Add Member
                </v-btn>
              </div>
            </div>
            <div class="ism-info-grid">
              <div class="ism-info-item ism-info-item--full">
                <div class="ism-info-icon" style="background: rgba(242, 101, 34, 0.1)">
                  <v-icon size="18" color="#F26522">mdi-map-marker-outline</v-icon>
                </div>
                <div class="ism-info-content">
                  <span class="ism-info-label">Address</span>
                  <span class="ism-info-value">{{ society.address }}</span>
                </div>
              </div>
              <div class="ism-info-item">
                <div class="ism-info-icon" style="background: rgba(59, 130, 246, 0.1)">
                  <v-icon size="18" color="#3b82f6">mdi-city-variant-outline</v-icon>
                </div>
                <div class="ism-info-content">
                  <span class="ism-info-label">Society Name</span>
                  <span class="ism-info-value">{{ society.name || "—" }}</span>
                </div>
              </div>
              <div class="ism-info-item">
                <div class="ism-info-icon" style="background: rgba(16, 185, 129, 0.1)">
                  <v-icon size="18" color="#10b981">mdi-check-circle-outline</v-icon>
                </div>
                <div class="ism-info-content" style="flex: 1">
                  <span class="ism-info-label">Platform Status</span>
                  <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                    <span class="ism-info-value" :style="{ color: society.is_active ? '#10b981' : '#ef4444' }">
                      {{ society.is_active ? "Active" : "Inactive" }}
                    </span>
                    <v-switch
                      v-model="society.is_active"
                      @change="toggleStatus"
                      dense
                      hide-details
                      color="#10b981"
                      class="ma-0 pa-0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Horizontal Divider between Society and Contact -->
          <div class="ism-horizontal-divider"></div>

          <!-- Contact Info (below Society Info) -->
          <div class="ism-detail-panel">
            <div class="ism-panel-title">
              <v-icon size="16" color="var(--accent)" class="mr-2">mdi-card-account-phone-outline</v-icon>
              Contact Information
            </div>
            <div class="ism-info-grid">
              <div class="ism-info-item ism-info-item--full">
                <div class="ism-info-icon" style="background: rgba(139, 92, 246, 0.1)">
                  <v-icon size="18" color="#8b5cf6">mdi-account-tie-outline</v-icon>
                </div>
                <div class="ism-info-content">
                  <span class="ism-info-label">Contact Person</span>
                  <span class="ism-info-value">{{ society.contact_person_name || "—" }}</span>
                </div>
              </div>
              <div class="ism-info-item ism-info-item--full">
                <div class="ism-info-icon" style="background: rgba(245, 158, 11, 0.1)">
                  <v-icon size="18" color="#f59e0b">mdi-phone-outline</v-icon>
                </div>
                <div class="ism-info-content">
                  <span class="ism-info-label">Mobile Number</span>
                  <span class="ism-info-value">{{ formatPhone(society.country_code, society.contact_person_number) }}</span>
                </div>
              </div>
              <div class="ism-info-item ism-info-item--full">
                <div class="ism-info-icon" style="background: rgba(239, 68, 68, 0.1)">
                  <v-icon size="18" color="#ef4444">mdi-email-outline</v-icon>
                </div>
                <div class="ism-info-content">
                  <span class="ism-info-label">Email Address</span>
                  <span class="ism-info-value" style="word-break: break-word">{{ society.contact_person_email || "—" }}</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Vertical Divider -->
        <div class="ism-detail-vdivider"></div>

        <!-- Right: Subscription Info -->
        <div class="ism-detail-panel">
          <div class="ism-panel-title">
            <v-icon size="18" color="var(--accent)" class="mr-2">mdi-diamond-stone</v-icon>
            Subscription
          </div>

          <div class="ism-sub-content">
            <template v-if="currentPlan && (currentPlan.expiration_date || (currentPlan.plan && currentPlan.plan.title))">

              <!-- Current Plan Card -->
              <div class="ism-plan-card" :class="isPlanExpired ? 'plan-expired' : 'plan-active'">
                <div class="ism-plan-card-top">
                  <div>
                    <div class="ism-plan-tier-label">Current Plan</div>
                    <div class="ism-plan-name">
                      {{ currentPlan.plan ? currentPlan.plan.title : 'Standard' }}
                      <span class="ism-plan-suffix">Plan</span>
                    </div>
                  </div>
                  <div class="ism-plan-status-badge" :class="isPlanExpired ? 'expired' : 'active'">
                    <span class="status-dot"></span>
                    {{ isPlanExpired ? 'Expired' : 'Active' }}
                  </div>
                </div>

                <div class="ism-plan-card-sep"></div>

                <div class="ism-plan-card-bottom">
                  <div class="ism-plan-price-block">
                    <span class="price-label">Price</span>
                    <div class="ism-plan-price">
                      <span class="price-sym">₹</span>
                      <span class="price-val">{{ Number(currentPlan.plan ? (currentPlan.plan.amount || 0) : 0).toLocaleString('en-IN') }}</span>
                      <span class="price-per">/ plan</span>
                    </div>
                  </div>
                  <div class="ism-plan-expiry-block" :class="{ 'is-expired': isPlanExpired }" style="margin-left: auto; flex: 0 0 auto;">
                    <span class="expiry-label">{{ isPlanExpired ? 'Expired on' : 'Valid until' }}</span>
                    <div class="expiry-date">
                      <v-icon size="14" :color="isPlanExpired ? '#f87171' : '#34d399'" class="mr-1">mdi-calendar-check-outline</v-icon>
                      {{ formattedExpirationDate || 'N/A' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Next Plan Card -->
              <div v-if="nextPlan" class="ism-plan-card ism-next-plan-card">
                <div class="ism-plan-card-top">
                  <div>
                    <div class="ism-plan-tier-label" style="color: #8b5cf6;">Next Plan</div>
                    <div class="ism-plan-name">
                      {{ nextPlan.plan ? nextPlan.plan.title : 'Upcoming' }}
                      <span class="ism-plan-suffix">Plan</span>
                    </div>
                  </div>
                  <div class="ism-plan-status-badge" style="background: rgba(139,92,246,0.12); color: #8b5cf6; border-color: rgba(139,92,246,0.25);">
                    <span class="status-dot" style="background: #8b5cf6;"></span>
                    Scheduled
                  </div>
                </div>

                <div class="ism-plan-card-sep"></div>

                <div class="ism-plan-card-bottom">
                  <div class="ism-plan-price-block">
                    <span class="price-label">Price</span>
                    <div class="ism-plan-price">
                      <span class="price-sym">₹</span>
                      <span class="price-val">{{ Number(nextPlan.amount || (nextPlan.plan ? nextPlan.plan.amount : 0) || 0).toLocaleString('en-IN') }}</span>
                      <span class="price-per">/ plan</span>
                    </div>
                  </div>
                  <div class="ism-plan-expiry-block">
                    <span class="expiry-label">Start Date</span>
                    <div class="expiry-date">
                      <v-icon size="14" color="#8b5cf6" class="mr-1">mdi-calendar-arrow-right</v-icon>
                      {{ formatNextPlanDate(nextPlan.start_date) || 'N/A' }}
                    </div>
                  </div>
                  <div class="ism-plan-expiry-block">
                    <span class="expiry-label">Valid until</span>
                    <div class="expiry-date">
                      <v-icon size="14" color="#8b5cf6" class="mr-1">mdi-calendar-clock-outline</v-icon>
                      {{ formatNextPlanDate(nextPlan.expiration_date) || 'N/A' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Manage Expiration -->
              <div class="ism-manage-block">
                <div class="ism-manage-label">
                  <v-icon size="14" color="var(--accent)">mdi-pencil-outline</v-icon>
                  Manage Expiration
                </div>
                <div class="ism-date-picker-wrap">
                  <v-menu
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    :return-value.sync="currentPlan.expiration_date"
                    transition="slide-y-transition"
                    offset-y
                    min-width="290px"
                    content-class="ism-custom-datepicker-menu"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div class="ism-sub-input-container" v-bind="attrs" v-on="on">
                        <v-icon size="16" color="var(--accent)" class="mr-2">mdi-calendar-edit</v-icon>
                        <span class="ism-sub-input-val">{{ formattedExpirationDate || 'Select Date' }}</span>
                        <v-icon size="16" color="var(--text-secondary)" class="ml-auto">mdi-chevron-down</v-icon>
                      </div>
                    </template>
                    <v-date-picker
                      v-model="currentPlan.expiration_date"
                      no-title
                      :min="minDate"
                      scrollable
                      color="#F26522"
                      header-color="#F26522"
                      class="ism-custom-datepicker"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="var(--text-secondary)" class="ism-dp-btn" @click="menu = false">Cancel</v-btn>
                      <v-btn text color="#F26522" class="ism-dp-btn ism-dp-btn--ok" @click="$refs.menu.save(currentPlan.expiration_date)">OK</v-btn>
                    </v-date-picker>
                  </v-menu>
                  <button
                    class="btn-accent ism-sub-update-btn"
                    @click="updateDateChange(currentPlan.expiration_date)"
                  >
                    <v-icon size="15" color="#fff" class="mr-1">mdi-check</v-icon>
                    Update
                  </button>
                </div>
              </div>

            </template>
            <template v-else>
              <div class="ism-sub-empty">
                <div class="ism-empty-icon-wrap">
                  <v-icon size="36" color="var(--border-color2)">mdi-diamond-outline</v-icon>
                </div>
                <p>No active subscription found</p>
                <span style="font-size: 11px; color: var(--text-secondary); margin-top: 4px;">Initialize plan to see details</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- Confirm Extend Modal -->
    <ConfirmModal
      :isOpen="dialogDate"
      type="success"
      title="Confirm Extension"
      :message="`Extend the plan until ${getFormattedDate(newSelectedDate)}?`"
      confirmText="Yes, Extend"
      cancelText="Cancel"
      @confirm="extendDate"
      @cancel="dialogDate = false"
    />

    <!-- Add Member Modal Component -->
    <AddMemberModal
      ref="addMemberModal"
      :society_id="society_id"
      :society="society"
      @update-society="$emit('update-society')"
    />

    <!-- Add Unit Modal Component -->
    <AddUnitModal
      ref="addUnitModal"
      :society_id="society_id"
      :society="society"
      @unit-added="$emit('update-society')"
    />
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import ConfirmModal from "../../common/ConfirmModal.vue";
import AddMemberModal from "./AddMemberModal.vue";
import AddUnitModal from "./AddUnitModal.vue";

export default {
  name: "SocietyBasicInfo",
  components: { ConfirmModal, AddMemberModal, AddUnitModal },
  props: {
    society_id: String,
    society_prop: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: true,
      societyData: null,
      currentPlan: { expiration_date: null },
      nextPlan: null,
      menu: false,
      dialogDate: false,
      newSelectedDate: null,
      analyticsLoading: false,
      analytics: {},
      analyticsModules: [
        { key: 'members_count',    label: 'Members',    icon: 'mdi-account-group-outline', color: '#F26522', rgb: '242,101,34' },
        { key: 'vehicles_count',   label: 'Vehicles',   icon: 'mdi-car-outline',           color: '#14b8a6', rgb: '20,184,166' },
        { key: 'notices_count',    label: 'Notices',    icon: 'mdi-bullhorn-outline',       color: '#3b82f6', rgb: '59,130,246' },
        { key: 'complaints_count', label: 'Complaints', icon: 'mdi-chat-alert-outline',    color: '#ef4444', rgb: '239,68,68'  },
        { key: 'bills_count',      label: 'Bills',      icon: 'mdi-receipt-text-outline',  color: '#06b6d4', rgb: '6,182,212' },
        { key: 'events_count',     label: 'Events',     icon: 'mdi-calendar-star',         color: '#10b981', rgb: '16,185,129'},
        { key: 'visitors_count',   label: 'Visitors',   icon: 'mdi-badge-account-outline', color: '#f59e0b', rgb: '245,158,11'},
      ],
      residentTypes: [
        { id: 1, name: "Residential" },
        { id: 2, name: "Commercial" },
        { id: 3, name: "Residential & Commercial" },
      ],
      countries: [],
    };
  },

  computed: {
    society() {
      return this.society_prop || this.societyData || {};
    },
    storagePercent() {
      if (!this.society.storage_limit_mb || !this.society.storage_count) return 0;
      return Math.round((this.society.storage_count / this.society.storage_limit_mb) * 100);
    },
    cleanAddress() {
      if (!this.society.name) return "—";
      const city = this.society.city_name ? this.society.city_name : (this.society.other_city || this.society.city);
      const state = this.society.state_name ? this.society.state_name : (this.society.other_state || this.society.state);
      return (
        [this.society.address, city, state, this.society.country]
          .map(v => {
            if (v === this.society.country && !isNaN(v)) {
              const c = this.countries.find(curr => curr.id == v);
              return c ? c.name : v;
            }
            return v;
          })
          .filter(v => v && v !== "undefined" && v !== "null" && v !== "0" && v !== 0)
          .join(", ") || "—"
      );
    },
    formattedExpirationDate() {
      if (!this.currentPlan.expiration_date) return null;
      const d = new Date(this.currentPlan.expiration_date);
      const y = d.getFullYear();
      const m = (d.getMonth() + 1).toString().padStart(2, "0");
      const day = d.getDate().toString().padStart(2, "0");
      return `${day}-${m}-${y}`;
    },
    minDate() {
      if (this.currentPlan.expiration_date && new Date(this.currentPlan.expiration_date) < new Date()) {
        return new Date().toISOString().substr(0, 10);
      }
      return this.currentPlan.expiration_date || "";
    },
    isPlanExpired() {
      if (!this.currentPlan || !this.currentPlan.expiration_date) return false;
      const exp = new Date(this.currentPlan.expiration_date);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      exp.setHours(0, 0, 0, 0);
      return exp < now;
    },
  },

  watch: {
    society_prop: {
      handler(val) {
        if (val) {
          this.loading = false;
          if (!this.currentPlan.expiration_date && val.active_plan) {
            this.currentPlan = { ...val.active_plan };
          }
        }
      },
      immediate: true,
    },
    societyData: {
      handler(val) {
        if (val && val.active_plan && !this.currentPlan.expiration_date) {
          this.currentPlan = { ...val.active_plan };
        }
      }
    }
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    openAddMemberModal() {
      if (this.$refs.addMemberModal) {
        this.$refs.addMemberModal.openModal();
      }
    },

    openAddUnitModal() {
      if (this.$refs.addUnitModal) {
        this.$refs.addUnitModal.openModal();
      }
    },

    residentType(type) {
      const t = this.residentTypes.find(r => r.id == type);
      return t ? t.name : "N/A";
    },

    formatCurrency(c) {
      if (!c) return "—";
      const l = c.toLowerCase();
      if (l === "rs" || l === "inr") return "₹ INR";
      return c.toUpperCase();
    },

    formatPhone(code, number) {
      if (!number) return "—";
      const prefix = code ? (code.startsWith("+") ? code : "+" + code) : "";
      return `${prefix} ${number}`.trim();
    },

    getFormattedDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
    },

    updateDateChange(date) {
      this.newSelectedDate = date;
      this.dialogDate = true;
    },

    extendDate() {
      this.dialogDate = false;
      this.$axios
        .put(`superadmin/society-plan/${this.society_id}`, {
          expiration_date: this.newSelectedDate,
        })
        .then(res => {
          if (res.data.status) {
            this.getUserPlan();
            this.setAlert({ showAlert: true, variant: "green accent-4", timeout: 3000, message: res.data.message });
          } else {
            this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 3000, message: res.data.message });
          }
        });
    },

    formatStorage(mb) {
      if (!mb) return '0 MB';
      if (mb >= 1024) return (mb / 1024).toFixed(2) + ' GB';
      return mb.toFixed(1) + ' MB';
    },

    formatNextPlanDate(date) {
      if (!date) return null;
      const d = new Date(date);
      const y = d.getFullYear();
      const m = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      return `${day}-${m}-${y}`;
    },

    getSocietyAnalytics() {
      this.analyticsLoading = true;
      this.$axios.get(`superadmin/society/${this.society_id}/analytics`).then(res => {
        this.analyticsLoading = false;
        if (res.data.status) this.analytics = res.data.analytics || {};
      }).catch(() => { this.analyticsLoading = false; });
    },

    toggleStatus() {
      this.$axios
        .post("superadmin/society/" + this.society_id + "/changeStatus")
        .then(res => {
          const data = res.data;
          if (data.status) {
            this.setAlert({ showAlert: true, variant: "green accent-4", timeout: 3000, message: data.message });
            this.$emit("update-society");
          } else {
            this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 3000, message: data.message });
            this.$emit("update-society");
          }
        });
    },

    getCountries() {
      this.$axios.get('society/countries').then(res => {
        if (res.data.status) this.countries = res.data.data;
      });
    },

    getSocietyDetailsFallback() {
      if (!this.society_prop) {
        this.loading = true;
        this.$axios
          .get(`superadmin/society/${this.society_id}`)
          .then(res => {
            if (res.data.status) this.societyData = res.data.society;
            this.loading = false;
          })
          .catch(() => { this.loading = false; });
      }
    },

    getUserPlan() {
      this.$axios.get(`superadmin/user-plan/${this.society_id}`).then(async res => {
        if (res.data.status && res.data.current) {
          this.currentPlan = res.data.current;
        } else if (this.society && this.society.active_plan) {
          this.currentPlan = { ...this.society.active_plan };
        }

        if (res.data.nextPlan) {
          const np = res.data.nextPlan;
          if (np.plan_id && !np.plan) {
            try {
              const planRes = await this.$axios.get(`superadmin/plan/${np.plan_id}`);
              if (planRes.data && planRes.data.plan) np.plan = planRes.data.plan;
            } catch (_) { /* ignore */ }
          }
          this.nextPlan = np;
        } else {
          this.nextPlan = null;
        }

        this.loading = false;
      }).catch(() => {
        if (this.society && this.society.active_plan) {
          this.currentPlan = { ...this.society.active_plan };
        }
        this.loading = false;
      });
    },
  },

  mounted() {
    this.getSocietyDetailsFallback();
    this.getUserPlan();
    this.getCountries();
  },
};
</script>

<style scoped>
.ism-tab-body {
  padding: 28px;
  display: flex;
  flex-direction: column;
}

/* ── Society Insights Wrapper ── */
.ism-insights-wrap {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 20px 22px;
  margin-bottom: 24px;
}

.ism-insights-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  flex-wrap: wrap;
  gap: 8px;
}
.ism-insights-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
}

/* ── Insight Cards Grid ── */
.ism-insights-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.insight-card {
  position: relative;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  overflow: hidden;
  transition: transform 0.18s, box-shadow 0.18s;
  cursor: default;
}
.insight-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(var(--mc-rgb, 0,0,0), 0.12);
}
.insight-card-inner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
}
.insight-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(var(--mc-rgb, 0,0,0), 0.09);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.insight-count-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.insight-count {
  font-size: 26px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1;
  letter-spacing: -0.02em;
}
.insight-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.insight-bar-accent {
  height: 3px;
  background: linear-gradient(to right, var(--mc, var(--accent)), transparent);
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}
.insight-pct {
  margin-left: auto;
  font-size: 11px;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 20px;
  flex-shrink: 0;
}
.insight-pct.safe   { background: rgba(16,185,129,0.12); color: #10b981; }
.insight-pct.warn   { background: rgba(245,158,11,0.12); color: #f59e0b; }
.insight-pct.danger { background: rgba(239,68,68,0.12);  color: #ef4444; }

/* ── Storage ── */
.ism-storage-track {
  width: 100%;
  height: 7px;
  background: var(--border-color);
  border-radius: 99px;
  overflow: hidden;
}
.ism-storage-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.7s cubic-bezier(0.4,0,0.2,1);
}
.ism-storage-fill.safe    { background: linear-gradient(to right, #10b981, #34d399); }
.ism-storage-fill.warning { background: linear-gradient(to right, #f59e0b, #fbbf24); }
.ism-storage-fill.danger  { background: linear-gradient(to right, #ef4444, #f87171); }
.ism-upgrade-nudge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--text-secondary);
}
.ism-upgrade-nudge span {
  color: var(--accent);
  text-decoration: underline;
  cursor: pointer;
}

/* ── Stat Cards ── */
.ism-stat-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}
.stat-card {
  position: relative;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 20px;
  overflow: hidden;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s;
  display: flex;
  flex-direction: column;
}
.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.08);
}
.stat-card::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 4px;
  background: var(--card-bar);
  z-index: 1;
}
.stat-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.stat-card-label {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.stat-card-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--icon-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-card-value {
  font-size: 28px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.1;
  margin-bottom: 4px;
  letter-spacing: -0.02em;
}
.stat-card-delta {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  opacity: 0.8;
}

/* ── Detail Grid ── */
.ism-detail-grid {
  display: flex;
  gap: 30px;
  padding: 0 0 30px 0;
  align-items: stretch;
}

/* Left column: Society + Contact stacked */
.ism-left-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ism-left-column .ism-detail-panel {
  flex: 1;
}

/* Horizontal divider between Society and Contact panels */
.ism-horizontal-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, var(--border-color2), transparent);
  margin: 20px 0;
  opacity: 0.8;
}

.ism-detail-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ism-detail-vdivider {
  width: 1px;
  background: linear-gradient(to bottom, transparent, var(--border-color2), transparent);
  flex-shrink: 0;
  opacity: 0.6;
}
.ism-panel-title {
  font-size: 12.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  border-bottom: 2px solid var(--border-color);
  padding-bottom: 10px;
}

/* ── Info Grid ── */
.ism-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  flex: 1;
  align-content: start;
}
.ism-info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.2s;
}
.ism-info-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-sm);
  border-color: rgba(242, 101, 34, 0.18);
  background: var(--card-bg);
}
.ism-info-item--full { grid-column: 1 / -1; }
.ism-info-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ism-info-content {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.ism-info-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
}
.ism-info-value {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
}

/* ── Subscription ── */
.ism-sub-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
}

/* ── Plan Card ── */
.ism-plan-card {
  position: relative;
  border-radius: 18px;
  padding: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-md);
  transition: all 0.25s ease;
}
.ism-plan-card.plan-active {
  border-left: 4px solid #F26522;
  background: linear-gradient(135deg, rgba(242,101,34,0.04) 0%, var(--card-bg) 40%);
}
.ism-plan-card.plan-expired {
  border-left: 4px solid #ef4444;
  background: linear-gradient(135deg, rgba(239,68,68,0.04) 0%, var(--card-bg) 40%);
}
.ism-plan-card.ism-next-plan-card {
  border-left: 4px solid #8b5cf6;
  background: linear-gradient(135deg, rgba(139,92,246,0.04) 0%, var(--card-bg) 40%);
  margin-top: 0;
}
.ism-plan-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.ism-plan-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.ism-plan-tier-label {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}
.ism-plan-name {
  font-size: 24px;
  font-weight: 900;
  color: var(--text-primary);
  line-height: 1.1;
  letter-spacing: -0.025em;
}
.ism-plan-suffix {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-left: 5px;
  letter-spacing: 0;
}

.ism-plan-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  flex-shrink: 0;
}
.ism-plan-status-badge.active {
  background: rgba(16,185,129,0.1);
  color: #10b981;
  border: 1px solid rgba(16,185,129,0.2);
}
.ism-plan-status-badge.expired {
  background: rgba(239,68,68,0.1);
  color: #ef4444;
  border: 1px solid rgba(239,68,68,0.2);
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.active .status-dot {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16,185,129,0.25);
  animation: pulse-dot 2.2s infinite;
}
.expired .status-dot { background: #ef4444; }
@keyframes pulse-dot {
  0%, 100% { box-shadow: 0 0 0 2px rgba(16,185,129,0.25); }
  50%       { box-shadow: 0 0 0 5px rgba(16,185,129,0.08); }
}

.ism-plan-card-sep {
  height: 1px;
  background: var(--border-color);
}

/* ── Bottom row ── */
.ism-plan-card-bottom {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: stretch;
}
.ism-plan-price-block {
  flex: 0 0 auto;
  min-width: 100px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.price-label {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.ism-plan-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
}
.price-sym {
  font-size: 15px;
  font-weight: 700;
  color: var(--accent);
}
.price-val {
  font-size: 26px;
  font-weight: 900;
  color: var(--text-primary);
  letter-spacing: -0.025em;
  line-height: 1;
}
.price-per {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-left: 3px;
}

.ism-plan-expiry-block {
  flex: 1;
  min-width: 110px;
  background: rgba(16,185,129,0.05);
  border: 1px solid rgba(16,185,129,0.15);
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ism-plan-expiry-block.is-expired {
  background: rgba(239,68,68,0.05);
  border-color: rgba(239,68,68,0.15);
}
.expiry-label {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.07em;
}
.expiry-date {
  font-size: 13px;
  font-weight: 800;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  letter-spacing: -0.01em;
  white-space: nowrap;   
}

/* ── Next Plan specific overrides ── */
.ism-next-plan-card .ism-plan-expiry-block {
  background: rgba(139,92,246,0.06);
  border-color: rgba(139,92,246,0.18);
  flex: 0 0 180px;  /* 160px - 180px */
  min-width: 180px;
}
.ism-next-plan-card .ism-plan-price-block {
  flex: 1;
}
.ism-next-plan-card .price-sym,
.ism-next-plan-card .price-val {
  color: #8b5cf6;
}
/* Current plan expiry block — same fixed width */
.ism-plan-card:not(.ism-next-plan-card) .ism-plan-expiry-block {
  flex: 0 0 180px;  /* 160px - 180px */
    min-width: 180px;
}

/* ── Manage Block ── */
.ism-manage-block {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ism-manage-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.ism-date-picker-wrap {
  display: flex;
  gap: 10px;
}
.ism-sub-input-container {
  flex: 1;
  height: 44px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
}
.ism-sub-input-container:hover {
  border-color: var(--accent);
  background: var(--card-bg);
}
.ism-sub-input-val {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
}
.ism-sub-update-btn {
  height: 44px;
  padding: 0 24px;
  border-radius: 10px;
  font-size: 13px;
  letter-spacing: 0.02em;
}

/* ── Datepicker ── */
.ism-custom-datepicker-menu {
  border-radius: 16px !important;
  box-shadow: 0 12px 40px rgba(0,0,0,0.13) !important;
  border: 1px solid var(--border-color) !important;
  overflow: hidden;
}
.ism-custom-datepicker {
  font-family: 'Inter', sans-serif !important;
  border-radius: 16px !important;
}
::v-deep .v-date-picker-header { padding: 16px 14px 8px; background: transparent !important; }
::v-deep .v-date-picker-header .v-btn .v-icon { color: var(--text-secondary) !important; }
::v-deep .v-date-picker-header .v-btn:hover .v-icon { color: var(--accent) !important; }
::v-deep .v-date-picker-header__value button { font-weight: 800 !important; color: var(--text-primary) !important; font-size: 15px !important; }
::v-deep .v-date-picker-table { padding: 0 12px 8px 12px !important; }
::v-deep .v-date-picker-table th { font-weight: 700 !important; color: var(--text-secondary) !important; font-size: 11px !important; text-transform: uppercase !important; }
::v-deep .v-date-picker-table .v-btn { font-weight: 600 !important; border-radius: 8px !important; color: var(--text-primary) !important; }
::v-deep .v-date-picker-table .v-btn:not(.v-btn--active):hover { color: var(--accent) !important; }
::v-deep .v-date-picker-table .v-btn:not(.v-btn--active):hover::before { background: rgba(242,101,34,0.08) !important; opacity: 1 !important; }
::v-deep .v-date-picker-table .v-btn--active { background-color: var(--accent) !important; color: #fff !important; box-shadow: 0 4px 12px rgba(242,101,34,0.35) !important; }
::v-deep .v-picker__actions { padding: 8px 12px 12px !important; border-top: 1px solid var(--border-color) !important; }
.ism-dp-btn { text-transform: none !important; font-weight: 700 !important; font-size: 13px !important; border-radius: 8px !important; }
.ism-dp-btn--ok { background: rgba(242,101,34,0.08) !important; margin-left: 4px; }

/* ── Accent Button ── */
.btn-accent {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, var(--accent), var(--accent-hover));
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 700;
  padding: 10px 18px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px var(--accent-glow);
  white-space: nowrap;
}
.btn-accent:hover { filter: brightness(1.1); transform: translateY(-1px); }

/* ── Responsive ── */
@media (max-width: 1200px) {
  .ism-detail-grid { flex-direction: column; gap: 20px; }
  .ism-detail-vdivider { width: 100%; height: 1px; background: linear-gradient(to right, transparent, var(--border-color2), transparent); }
  .ism-insights-grid { grid-template-columns: repeat(3, 1fr); }
  .ism-left-column { flex-direction: row; gap: 20px; }
  .ism-horizontal-divider { width: 1px; height: auto; margin: 0; background: linear-gradient(to bottom, transparent, var(--border-color2), transparent); }
}
@media (max-width: 900px) {
  .ism-insights-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .ism-tab-body { padding: 14px; }
  .ism-insights-wrap { padding: 14px; }
  .ism-stat-row { grid-template-columns: 1fr 1fr; gap: 10px; }
  .ism-insights-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .insight-card-inner { padding: 12px; gap: 10px; }
  .insight-count { font-size: 22px; }
  .ism-detail-grid { padding: 0 0 20px 0; }
  .ism-info-grid { grid-template-columns: 1fr; }
  .ism-plan-card-bottom { flex-direction: column; gap: 10px; }
  .ism-plan-price-block { min-width: unset; }
  .ism-plan-expiry-block { min-width: unset; }
  .ism-left-column { flex-direction: column; }
  .ism-horizontal-divider { width: 100%; height: 1px; margin: 16px 0; background: linear-gradient(to right, transparent, var(--border-color2), transparent); }
}
@media (max-width: 480px) {
  .ism-stat-row { grid-template-columns: 1fr !important; }
  .ism-insights-grid { grid-template-columns: 1fr !important; }
}
</style>

<!-- Global dark mode overrides for Vuetify date-picker -->
<style>
body.dark-mode .ism-custom-datepicker-menu,
.theme--dark .ism-custom-datepicker-menu {
  box-shadow: 0 16px 48px rgba(0,0,0,0.55) !important;
  border-color: rgba(255,255,255,0.1) !important;
}
body.dark-mode .ism-custom-datepicker-menu .v-picker,
body.dark-mode .ism-custom-datepicker-menu .v-picker__body,
.theme--dark .ism-custom-datepicker-menu .v-picker,
.theme--dark .ism-custom-datepicker-menu .v-picker__body { background: #1e293b !important; }
body.dark-mode .ism-custom-datepicker-menu .v-card,
.theme--dark .ism-custom-datepicker-menu .v-card { background: #1e293b !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-header__value button,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-header__value button { color: #f1f5f9 !important; font-weight: 800 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-header .v-btn .v-icon,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-header .v-btn .v-icon { color: #94a3b8 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-header .v-btn:hover .v-icon,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-header .v-btn:hover .v-icon { color: #f1f5f9 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table th,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table th { color: #64748b !important; font-weight: 700 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn { color: #cbd5e1 !important; font-weight: 600 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn.v-btn--outlined,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn.v-btn--outlined { border-color: #F26522 !important; color: #F26522 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn:not(.v-btn--active):hover::before,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn:not(.v-btn--active):hover::before { background: rgba(242,101,34,0.15) !important; opacity: 1 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn:not(.v-btn--active):hover,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn:not(.v-btn--active):hover { color: #F26522 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn--active,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn--active { background-color: #F26522 !important; color: #ffffff !important; box-shadow: 0 4px 14px rgba(242,101,34,0.45) !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn.v-btn--disabled,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn.v-btn--disabled { color: #334155 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-picker__actions,
.theme--dark .ism-custom-datepicker-menu .v-picker__actions { background: #1e293b !important; border-top: 1px solid rgba(255,255,255,0.07) !important; }
body.dark-mode .ism-custom-datepicker-menu .ism-dp-btn,
.theme--dark .ism-custom-datepicker-menu .ism-dp-btn { color: #94a3b8 !important; }
body.dark-mode .ism-custom-datepicker-menu .ism-dp-btn.ism-dp-btn--ok,
.theme--dark .ism-custom-datepicker-menu .ism-dp-btn.ism-dp-btn--ok { color: #F26522 !important; background: rgba(242,101,34,0.1) !important; }
</style>
